import { EntityType } from '@frontend/common';
import { DefaultPrimaryButton } from '@frontend/components/common';
import { defaultCellClassName } from '@frontend/components/list';
import { ReactiveObjectList } from '@frontend/components/list-v2';
import { Application } from '@frontend/functionality/types';
import { CommonMessage } from '@frontend/lang';
import { LaundryTransactionClient } from '@frontend/laundry-app/api';
import { LaundryTransaction } from '@frontend/laundry-app/types';
import { useModals } from '@frontend/modal';
import { transactionRequest } from '@frontend/transaction/events';
import { Transaction } from '@frontend/transaction/types';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { ReactiveDetailRegistryEntry, ReactiveDetailView } from '../../modal/reactive-detail-shell/reactive-detail.types';
import { triggerRefetch } from '../../redux/detail-modal-slice';
import { useAppDispatch } from '../../redux/store';
import { CancelTransactionActionButton } from './cancel-transaction-action/cancel-transaction-action.component';
import { ChangeTransactionStateModal } from './change-transaction-state-modal/change-transaction-state-modal.component';
import { DelegateTransactionActionButton, canDelegateTransaction } from './delegate-transaction-action/delegate-transaction-action.component';
import { previewLaundryShippingLabel } from './preview-laundry-shipping-label';
import { TransactionDetail } from './transaction-detail-modal.controller';
import {
    LaundryTransactionGeneralSection,
    TransactionDetailsCard,
    TransactionEventsSection,
    TransactionGeneralSection,
    TransactionHistorySection,
    TransactionLabelsSection,
    TransactionParcelsSection,
    TransactionPicturesAndCommentsSection,
    fetchTransactionDetail,
    isLaundryTransaction,
    isPudoOrNivalizTransaction,
    isPudoTransaction,
    useTransactionCollis
} from './transaction-detail-sections';

export type TransactionExtendedDetail = TransactionDetail | LaundryTransaction;

function isLaundryExtended(detail: TransactionExtendedDetail | null | undefined): detail is LaundryTransaction {
    return detail != null && isLaundryTransaction(detail);
}

function resolveApplication(view: ReactiveDetailView<Transaction, TransactionExtendedDetail>): Application {
    const fromContext = view.context.application;
    if (
        fromContext === Application.VENDING ||
        fromContext === Application.LENDING ||
        fromContext === Application.LAUNDRY ||
        fromContext === Application.NIVALIZ ||
        fromContext === Application.PUDO
    ) {
        return fromContext;
    }
    const extendedApp = view.extended && 'application' in view.extended ? view.extended.application : undefined;
    if (
        extendedApp === Application.VENDING ||
        extendedApp === Application.LENDING ||
        extendedApp === Application.LAUNDRY ||
        extendedApp === Application.NIVALIZ ||
        extendedApp === Application.PUDO
    ) {
        return extendedApp;
    }
    return Application.PUDO;
}

const TransactionHeaderActionsView = ({ view }: { view: ReactiveDetailView<Transaction, TransactionExtendedDetail> }) => {
    const { openModal } = useModals();
    const dispatch = useAppDispatch();
    const obj = view.extended;
    if (!obj) return null;

    if (isLaundryExtended(obj)) {
        return (
            <DefaultPrimaryButton
                text={
                    <FormattedMessage
                        id='transaction.preview_shipping_label'
                        description='Action label for opening the shipping label preview from a transaction detail modal'
                        defaultMessage='Preview shipping label'
                    />
                }
                onClick={() => {
                    if (obj.tracking_number) void previewLaundryShippingLabel(obj.tracking_number, openModal);
                }}
                disabled={!obj.tracking_number}
                size='sm'
                dataTestId='preview-laundry-shipping-label'
            />
        );
    }

    return (
        <>
            <DefaultPrimaryButton
                text={CommonMessage.BUTTONS.CHANGE_STATUS}
                onClick={() =>
                    openModal(
                        `change-transaction-state-modal-${obj.id}`,
                        () => (
                            <ChangeTransactionStateModal
                                transactions={[obj]}
                                onSubmitCallback={() => dispatch(triggerRefetch({ entityType: EntityType.TRANSACTION, entityId: obj.id }))}
                            />
                        ),
                        {},
                        { className: 'w-[390px]' }
                    )
                }
                size='sm'
            />
            {isPudoOrNivalizTransaction(obj) && canDelegateTransaction(view.permissions, obj) && (
                <DelegateTransactionActionButton
                    transaction={obj}
                    onSubmitCallback={() => dispatch(triggerRefetch({ entityType: EntityType.TRANSACTION, entityId: obj.id }))}
                />
            )}
            <CancelTransactionActionButton
                transaction={obj}
                canRender
                onSubmitCallback={() => dispatch(triggerRefetch({ entityType: EntityType.TRANSACTION, entityId: obj.id }))}
            />
        </>
    );
};

const TransactionReactiveTab = ({
    view,
    tab
}: {
    view: ReactiveDetailView<Transaction, TransactionExtendedDetail>;
    tab: 'general' | 'parcels' | 'pictures_and_comments' | 'labels' | 'history' | 'events' | 'content';
}) => {
    const dispatch = useAppDispatch();
    const { openModal, closeModal } = useModals();
    const obj = view.extended;
    const collis = useTransactionCollis(obj && !isLaundryExtended(obj) ? obj : null);

    if (!obj) return null;

    if (isLaundryExtended(obj)) {
        if (tab === 'general') {
            return (
                <>
                    <TransactionDetailsCard
                        obj={obj}
                        collis={null}
                    />
                    <LaundryTransactionGeneralSection obj={obj} />
                </>
            );
        }
        if (tab !== 'content') return null;
        return (
            <ReactiveObjectList<{ id: string; serial_number: string; product: string; product_attributes: Record<string, string> }>
                dispatch={dispatch}
                useStaticListId
                label='Content'
                id={`${obj.id}-laundry-items`}
                fetch={async () => ({
                    count: obj.contents.length,
                    results: obj.contents.map((c) => ({
                        id: c.id,
                        serial_number: c.equipment_serial_number,
                        product: c.product_name,
                        product_attributes: c.product_attributes
                    }))
                })}
                columns={{
                    columns: ['serial_number', 'product', 'product_attributes'],
                    headerOverwrite: new Map([
                        ['serial_number', 'ID'],
                        ['product_attributes', 'Attributes']
                    ]),
                    customCells: new Map([
                        [
                            'product_attributes',
                            (cell) => (
                                <td className={defaultCellClassName}>
                                    <div className='inline-flex gap-2 text-black'>
                                        {Object.entries(cell.row.original.product_attributes ?? {}).map(([key, value]) => (
                                            <span key={key}>
                                                {key}: {value}
                                            </span>
                                        ))}
                                    </div>
                                </td>
                            )
                        ]
                    ])
                }}
                type={EntityType.COLLI_CONTENT}
                tableProps={{ hideFooter: true, defaultHeight: true }}
            />
        );
    }

    if (tab === 'general') {
        return (
            <>
                <TransactionDetailsCard
                    obj={obj}
                    collis={collis}
                />
                <TransactionGeneralSection obj={obj} />
            </>
        );
    }
    if (tab === 'parcels') {
        return (
            <TransactionParcelsSection
                obj={obj}
                viewProps={{ openModal, closeModal } as never}
                dispatch={dispatch}
            />
        );
    }
    if (tab === 'pictures_and_comments') return <TransactionPicturesAndCommentsSection obj={obj} />;
    if (tab === 'labels') {
        return (
            <TransactionLabelsSection
                obj={obj}
                viewProps={{ openModal, closeModal } as never}
                dispatch={dispatch}
            />
        );
    }
    if (tab === 'history') return <TransactionHistorySection obj={obj} />;
    if (tab === 'events') return <TransactionEventsSection obj={obj} />;
    return null;
};

export const transactionReactiveDetail: ReactiveDetailRegistryEntry<Transaction, TransactionExtendedDetail> = {
    entityType: EntityType.TRANSACTION,
    resolveFunction: transactionRequest,
    fetchExtended: ({ object, context }) => {
        const application = (context.application as Application | undefined) ?? (object.application as Application | undefined) ?? Application.PUDO;
        if (application === Application.LAUNDRY) {
            return LaundryTransactionClient.fetchLaundryTransaction(object.account_id, object.id);
        }
        return fetchTransactionDetail(application, object.account_id, object.id);
    },
    title: ({ extended, object }) => (
        <>
            {CommonMessage.OBJECTS.TRANSACTION.DEFAULT} {extended?.tracking_number ?? object.tracking_number ?? object.id}
        </>
    ),
    headerActions: (view) => <TransactionHeaderActionsView view={view} />,
    tabs: [
        {
            id: 'general',
            label: CommonMessage.DETAIL.GENERAL,
            render: (view) => (
                <TransactionReactiveTab
                    view={view}
                    tab='general'
                />
            )
        },
        {
            id: 'parcels',
            label: CommonMessage.OBJECTS.PARCEL.PLURAL,
            visible: (view) => {
                const obj = view.extended;
                return obj != null && !isLaundryExtended(obj) && isPudoTransaction(obj);
            },
            render: (view) => (
                <TransactionReactiveTab
                    view={view}
                    tab='parcels'
                />
            )
        },
        {
            id: 'pictures_and_comments',
            label: (
                <FormattedMessage
                    id='transaction.detailModal.PicturesAndComments'
                    description='Tab label in the transaction detail modal for attachments and transaction comments'
                    defaultMessage='Pictures & comments'
                />
            ),
            visible: (view) => view.extended != null && !isLaundryExtended(view.extended),
            render: (view) => (
                <TransactionReactiveTab
                    view={view}
                    tab='pictures_and_comments'
                />
            )
        },
        {
            id: 'labels',
            label: CommonMessage.OBJECTS.LABEL.PLURAL,
            visible: (view) => {
                const obj = view.extended;
                return obj != null && !isLaundryExtended(obj) && isPudoOrNivalizTransaction(obj);
            },
            render: (view) => (
                <TransactionReactiveTab
                    view={view}
                    tab='labels'
                />
            )
        },
        {
            id: 'history',
            label: CommonMessage.DETAIL.HISTORY,
            visible: (view) => view.extended != null && !isLaundryExtended(view.extended),
            render: (view) => (
                <TransactionReactiveTab
                    view={view}
                    tab='history'
                />
            )
        },
        {
            id: 'events',
            label: CommonMessage.DETAIL.EVENTS,
            visible: (view) => view.extended != null && !isLaundryExtended(view.extended),
            render: (view) => (
                <TransactionReactiveTab
                    view={view}
                    tab='events'
                />
            )
        },
        {
            id: 'content',
            label: (
                <FormattedMessage
                    id='platform.transaction.modal.transaction.reactive-detail.laundry-content'
                    description='Tab label for laundry shipment contents on the transaction detail pane'
                    defaultMessage='Content'
                />
            ),
            visible: (view) => isLaundryExtended(view.extended) || resolveApplication(view) === Application.LAUNDRY,
            render: (view) => (
                <TransactionReactiveTab
                    view={view}
                    tab='content'
                />
            )
        }
    ]
};
